import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Clapperboard, FileText, Folder } from 'lucide-react';
import api from '../lib/api';
import CommandPalette, { useCommandPaletteHotkey } from '../ui/CommandPalette';

// The archive API the Storage page reads (folders across every client the user can read).
const ARCHIVE = '/api/storage-archive';
const PER_GROUP = 8;

// §2.5 Command palette on Console and Workbench, wired to real search through the existing list endpoints:
// script titles, production titles, clients, footage (storage) folders, and every nav destination.
// Loaded once on first open, then searched in memory. Ctrl K / ⌘K anywhere.
export default function ShellPalette({ nav, open, setOpen }) {
  const navigate = useNavigate();
  useCommandPaletteHotkey(setOpen);
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!open || started.current) return;
    started.current = true;
    setLoading(true);
    const settled = p => p.then(r => r.data).catch(() => null);
    Promise.all([
      settled(api.get('/api/scripts')),
      settled(api.get('/api/scripts?qualStatus=Filming')),
      settled(api.get('/api/live-production')),
      settled(api.get('/api/clients')),
      settled(api.get(`${ARCHIVE}/folders`)),
    ]).then(([listed, shelf, cards, clients, folders]) => {
      const scripts = [...new Map([...(listed || []), ...(shelf || [])].map(s => [s.id, s])).values()].filter(s => !s.isDeleted);
      const titleOf = Object.fromEntries(scripts.map(s => [s.id, s.scriptName]));
      const clientName = Object.fromEntries((clients || []).map(c => [c.id, c.name]));
      setIndex({
        scripts: scripts.map(s => ({ id: `s-${s.id}`, label: s.scriptName, group: 'Scripts', icon: FileText, meta: s.client?.name, to: `/scripts?script=${s.id}` })),
        productions: (cards || []).filter(c => !c.isArchived && titleOf[c.scriptId]).map(c => ({ id: `p-${c.id}`, label: titleOf[c.scriptId], group: 'Productions', icon: Clapperboard, meta: c.status, to: `/production/${c.id}` })),
        clients: (clients || []).map(c => ({ id: `c-${c.id}`, label: c.name, group: 'Clients', icon: Building2, meta: 'Storage', to: `/storage?clientId=${c.id}` })),
        folders: ((folders && folders.folders) || []).map(f => ({ id: `f-${f.id}`, label: f.path || f.name, group: 'Footage folders', icon: Folder, meta: clientName[f.clientId], to: `/storage?clientId=${f.clientId}&folderId=${f.id}` })),
        failed: [listed, cards, clients, folders].some(x => x == null),
      });
    }).finally(() => setLoading(false));
  }, [open]);

  const pages = nav.flatMap(g => g.items.map(i => ({ id: `n-${i.to}`, label: i.label, group: 'Pages', icon: i.icon, meta: g.group, to: i.to })));
  const source = useCallback(q => {
    const k = q.trim().toLowerCase();
    const hit = xs => (k ? xs.filter(x => x.label?.toLowerCase().includes(k)) : xs);
    const take = xs => hit(xs).slice(0, PER_GROUP);
    const groups = k ? [take(pages), ...(index ? [take(index.scripts), take(index.productions), take(index.clients), take(index.folders)] : [])] : [pages];
    return groups.flat().map(x => ({ ...x, onSelect: () => navigate(x.to) }));
  }, [pages, index, navigate]);

  return <CommandPalette open={open} onOpenChange={setOpen} source={source} loading={loading && !index} placeholder="Search scripts, productions, clients, folders, pages" />;
}
